import React, { useCallback, useState } from "react";
import { Button, ButtonGroup } from "reactstrap";
import type { GameChartMode } from "./GameChart";

const KEY = "deja-game-chart-mode";

/**
 * Line-vs-candles preference, remembered across rounds and across the two surfaces that
 * draw a round (the game page and the homepage teaser). Reaching for candles is a habit,
 * not a per-round choice, so re-picking it on every chart would be the annoying part.
 */
export function useGameChartMode(): [GameChartMode, (m: GameChartMode) => void] {
  const [mode, setMode] = useState<GameChartMode>(() => {
    try {
      return localStorage.getItem(KEY) === "candles" ? "candles" : "line";
    } catch {
      return "line"; // storage can throw outright in private/blocked contexts
    }
  });
  const set = useCallback((m: GameChartMode) => {
    setMode(m);
    try {
      localStorage.setItem(KEY, m);
    } catch {
      /* preference just won't persist */
    }
  }, []);
  return [mode, set];
}

/**
 * Renders nothing unless the round actually has candle data — an older round has no OHLC,
 * and a toggle that silently does nothing is worse than no toggle.
 */
export default function GameChartToggle({
  mode,
  onChange,
  available,
}: {
  mode: GameChartMode;
  onChange: (m: GameChartMode) => void;
  available: boolean;
}) {
  if (!available) return null;
  return (
    <ButtonGroup size="sm">
      <Button
        color="secondary"
        outline={mode !== "line"}
        onClick={() => onChange("line")}
        aria-pressed={mode === "line"}
        title="Line"
      >
        Line
      </Button>
      <Button
        color="secondary"
        outline={mode !== "candles"}
        onClick={() => onChange("candles")}
        aria-pressed={mode === "candles"}
        title="Candles"
      >
        Candles
      </Button>
    </ButtonGroup>
  );
}
